import { c as _c } from "react/compiler-runtime";
import type { QuickSearchShortcut } from '@/contexts/NavigationContext/NavigationContext';
import { useNavigationContext } from '@/contexts/NavigationContext/NavigationContext';
import type { ISearchContextValue } from '@/contexts/SearchContext';
import { useSearchContext } from '@/contexts/SearchContext';
import { useSearchRelatedContent } from '@/hooks/useSearchRelatedContent';
import type { IResultItem } from '@/interfaces';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import type { TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { TrackingAction } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { logger } from '@/utils/logger/logger';
import type { Context } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { FCh } from 'react-fc';
const MAX_JUMP_TO_RESULTS = 5;
type QuickSearchContentGroup = {
  totalResults?: number;
  items: IResultItem[];
};
export type QuickSearchContentResults = {
  totalResults: number;
  resultGroups: {
    research?: QuickSearchContentGroup;
    news?: QuickSearchContentGroup;
    data?: QuickSearchContentGroup;
    event?: QuickSearchContentGroup;
    authors?: QuickSearchContentGroup;
  };
};
export type RelatedContentResults = {
  relatedCountries?: any[];
  relatedSectors?: any[];
};
interface IGlobalSearchContextValue {
  currentJumpToResults?: any[];
  globalSearchResults?: QuickSearchContentResults;
  relatedContent?: RelatedContentResults;
  activeQuery: string;
  setActiveQuery: (param: string) => void;
  isLoading: boolean;
}
const initialContextState: IGlobalSearchContextValue = {
  globalSearchResults: undefined,
  relatedContent: undefined,
  activeQuery: '',
  setActiveQuery: () => {},
  isLoading: false
};
const GlobalSearchContext: Context<IGlobalSearchContextValue> = createContext(initialContextState);
const doJumpToQuery = (currentQuery: string | undefined, availableNavigationLinks: QuickSearchShortcut[], relatedContent: RelatedContentResults) => {
  const normalizedQuery = (currentQuery || '').toLowerCase().trim();
  if (!(normalizedQuery && normalizedQuery.length >= 2)) {
    return [];
  }
  const relatedCountries = relatedContent?.relatedCountries || [];
  const relatedSectors = relatedContent?.relatedSectors || [];
  const relatedContentNames = [...relatedCountries.map(content => (content.name || content.title).toLowerCase()), ...relatedSectors.map(content => (content.name || content.title).toLowerCase())];
  const searchTerms = [normalizedQuery, ...relatedContentNames];
  return availableNavigationLinks.filter(linkMetadata => linkMetadata.matches.some(pattern => searchTerms.some(term => pattern.includes(term)))).slice(0, MAX_JUMP_TO_RESULTS);
};
export const GlobalSearchContextProvider: FCh<{
  trackingCategory?: TrackingCategory;
}> = ({
  children,
  trackingCategory
}) => {
  const {
    availableNavigationLinks
  } = useNavigationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [activeQuery, setActiveQuery] = useState('');
  const [results, setResults] = useState<QuickSearchContentResults>();
  const {
    relatedContent
  } = useSearchRelatedContent(activeQuery, 0); // No debounce for quick search
  const requestCounter = useRef(0);
  const lastAppliedRequest = useRef(0);
  const currentJumpToResults = useMemo(() => activeQuery ? doJumpToQuery(activeQuery, availableNavigationLinks, relatedContent || {}) : [], [activeQuery, availableNavigationLinks, relatedContent]);
  useEffect(() => {
    if (!activeQuery) {
      // Disown anything still in flight so it cannot repopulate a cleared box
      lastAppliedRequest.current = requestCounter.current;
      setIsLoading(false);
      setResults(undefined);
      return;
    }
    const requestId = ++requestCounter.current;
    setIsLoading(true);
    trackEvent(TrackingAction.Search, trackingCategory, activeQuery);
    (async () => {
      try {
        const searchResults = await new BrowserHttpClient().get({
          path: '/quick_search',
          query: {
            q: activeQuery
          }
        });

        // A later requests has already landed. This response is too slow, discarding
        if (requestId <= lastAppliedRequest.current) {
          return;
        }
        lastAppliedRequest.current = requestId;
        setResults(searchResults);
      } catch (e) {
        logger.error('Error fetching quick search results:', e);
      } finally {
        // Keep the spinner up until the newest query lands, even though
        // earlier ones have already painted
        if (requestId === requestCounter.current) {
          setIsLoading(false);
        }
      }
    })();
  }, [activeQuery]);
  return <GlobalSearchContext.Provider value={{
    currentJumpToResults,
    globalSearchResults: results,
    relatedContent,
    activeQuery,
    setActiveQuery,
    isLoading
  }}>
            {children}
        </GlobalSearchContext.Provider>;
};
export const useGlobalSearchContext = () => {
  const $ = _c(3);
  const searchContext = useSearchContext();
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error("useActiveSearchContext must be used within an ActiveSearchContextProvider");
  }
  let t0;
  if ($[0] !== context || $[1] !== searchContext) {
    t0 = {
      ...searchContext,
      ...context
    };
    $[0] = context;
    $[1] = searchContext;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};